import React, { useState } from 'react';
import { useListStudentFeesQuery, useDeleteFeeMutation } from '../../store/api/localApi';
import { AddFeeModal } from './AddFeeModal';
import type { Fee } from '../../types/fee';

interface Props {
  studentId: string;
  isAdmin: boolean;
}

export function formatCurrency(amount: number): string {
  // Irish product default: euro. No per-org/location currency config exists on
  // this component's props/context yet, so this is the correct hardcode.
  return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' }).format(amount);
}

export const FeesSection: React.FC<Props> = ({ studentId, isAdmin }) => {
  const { data, isLoading, isError } = useListStudentFeesQuery(studentId);
  const [deleteFee] = useDeleteFeeMutation();

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingFee, setEditingFee] = useState<Fee | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const fees = data?.fees ?? [];

  const handleDelete = async (feeId: string) => {
    try {
      await deleteFee({ studentId, feeId }).unwrap();
    } finally {
      setPendingDeleteId(null);
    }
  };

  return (
    <section data-testid="fees-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">Fees</h3>
        {isAdmin && (
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            data-testid="add-fee-btn"
          >
            + Add fee
          </button>
        )}
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading fees…</p>}

      {isError && <p className="text-sm text-red-500">Could not load fees.</p>}

      {!isLoading && !isError && fees.length === 0 && (
        <p className="text-sm text-gray-400 italic" data-testid="fees-empty">
          No fees recorded for this student.
        </p>
      )}

      {!isLoading && fees.length > 0 && (
        <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
          {fees.map((fee) => (
            <li
              key={fee.id}
              className="flex items-center justify-between px-4 py-3"
              data-testid={`fee-row-${fee.id}`}
            >
              <div className="flex-1 min-w-0">
                <p
                  className="text-sm font-medium text-gray-900 truncate"
                  data-testid={`fee-label-${fee.id}`}
                >
                  {fee.label}
                </p>
                <p className="text-sm text-gray-500" data-testid={`fee-amount-${fee.id}`}>
                  {formatCurrency(fee.amount)}
                </p>
              </div>

              {isAdmin && (
                <div className="flex items-center gap-2 ml-4 shrink-0">
                  {pendingDeleteId === fee.id ? (
                    <>
                      <span className="text-xs text-gray-500">Delete?</span>
                      <button
                        type="button"
                        onClick={() => handleDelete(fee.id)}
                        className="text-xs text-red-600 hover:text-red-700 font-medium"
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        onClick={() => setPendingDeleteId(null)}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        No
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => setEditingFee(fee)}
                        className="text-xs text-gray-500 hover:text-gray-700"
                        data-testid={`fee-edit-${fee.id}`}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setPendingDeleteId(fee.id)}
                        className="text-xs text-red-500 hover:text-red-700"
                        data-testid={`fee-delete-${fee.id}`}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {showAddModal && <AddFeeModal studentId={studentId} onClose={() => setShowAddModal(false)} />}

      {editingFee && (
        <AddFeeModal studentId={studentId} fee={editingFee} onClose={() => setEditingFee(null)} />
      )}
    </section>
  );
};
